/**
 * Language selector component.
 * @module components/LanguageSelector/LanguageSelector
 */

import PropTypes from 'prop-types';
import { useState } from 'react';
import { useSelector } from 'react-redux';

import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import {
  toReactIntlLang,
  toBackendLang,
} from '@plone/volto/helpers/Utils/Utils';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';

import config from '@plone/volto/registry';

import Button from '../Button';
import Icon from '../Icon';
import find from 'lodash/find';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import LanguageMap from '@plone/volto/helpers/LanguageMap/LanguageMap';

const LanguageButton = ({
  languages = [],
  translations,
  currentLang,
  onClickAction,
}) => {
  const availableLanguage = languages.filter(
    (lang) => lang.value !== toBackendLang(currentLang),
  )[0];

  const translation = find(translations, { language: availableLanguage.value });

  const url = translation
    ? flattenToAppURL(translation['@id'])
    : `/${availableLanguage.value}`;

  return (
    <Button
      link
      href={url}
      onClick={() => {
        onClickAction();
      }}
    >
      <Icon name="language" />
      {availableLanguage.title}
    </Button>
  );
};

const LanguageDropdown = ({
  languages = [],
  translations,
  currentLang,
  onClickAction,
}) => {
  const [open, setOpen] = useState(false);

  const availableLanguages = languages.filter(
    (lang) => lang.value !== toBackendLang(currentLang),
  );

  return (
    <>
      <Button link onClick={() => setOpen(!open)}>
        <Icon name="language" />
        {LanguageMap[currentLang].nativeName}
        <Icon name="keyboard_arrow_down" bold />
      </Button>

      {open && (
        <div className="options">
          {availableLanguages.map((lang) => {
            const translation = find(translations, { language: lang.value });

            const url = translation
              ? flattenToAppURL(translation['@id'])
              : `/${lang.value}`;

            return (
              <UniversalLink
                key={lang.value}
                href={url}
                onClick={() => {
                  setOpen(false);
                  onClickAction();
                }}
              >
                {lang.title}
              </UniversalLink>
            );
          })}
        </div>
      )}
    </>
  );
};

const LanguageSelector = (props) => {
  const currentLang = useSelector((state) => state.intl.locale);
  const translations = useSelector(
    (state) => state.content.data?.['@components']?.translations?.items,
  );
  const { supportedLanguages, isMultilingual, defaultLanguage } =
    config?.settings;

  if (!isMultilingual) {
    return (
      <Helmet>
        <html lang={toReactIntlLang(defaultLanguage)} />
      </Helmet>
    );
  }

  const languageList = supportedLanguages.map((lang) => {
    return {
      title: LanguageMap[lang]?.nativeName,
      value: lang,
    };
  });

  const isDropdown = languageList.length > 1;

  return (
    <div className="language-selector">
      {isDropdown ? (
        <LanguageDropdown
          languages={languageList}
          currentLang={currentLang}
          translations={translations}
          onClickAction={props.onClickAction}
        />
      ) : (
        <LanguageButton
          languages={languageList}
          currentLang={currentLang}
          translations={translations}
          onClickAction={props.onClickAction}
        />
      )}
    </div>
  );
};

LanguageSelector.propTypes = {
  onClickAction: PropTypes.func,
};

LanguageSelector.defaultProps = {
  onClickAction: () => {},
};

export default LanguageSelector;
